import { useRouterState } from '@tanstack/react-router'
import { useMemo } from 'react'

/**
 * Leitura da query string da rota atual.
 *
 * Lemos de `location.searchStr` (a string crua) em vez de usar
 * `useSearch({ from: '...' })` porque as rotas do painel são declaradas em
 * código, e o `from` obrigaria cada tela a repetir o ID interno da rota — uma
 * string que muda quando a árvore é reorganizada e que o TypeScript não
 * consegue conferir contra o arquivo da tela. Com `URLSearchParams`, o
 * contrato da tela é o parâmetro que ela lê, e nada mais.
 */
export function useParametrosDaBusca(): URLSearchParams {
  const searchStr = useRouterState({ select: (estado) => estado.location.searchStr })

  return useMemo(() => new URLSearchParams(searchStr), [searchStr])
}

/** Um parâmetro da query string. `null` quando ausente ou vazio. */
export function useParametroDaBusca(nome: string): string | null {
  const parametros = useParametrosDaBusca()
  const valor = parametros.get(nome)

  return valor === null || valor.length === 0 ? null : valor
}

/**
 * Destino de redirecionamento seguro.
 *
 * Só aceita caminho interno começando com uma única barra. `//evil.com` e
 * `https://evil.com` são URLs ABSOLUTAS para o navegador — aceitá-las aqui
 * transformaria o parâmetro `redirecionar` do login em um redirecionamento
 * aberto, que é como se monta phishing usando o domínio da própria vítima.
 */
export function destinoInternoSeguro(valor: string | null, padrao = '/'): string {
  if (valor === null || !valor.startsWith('/') || valor.startsWith('//')) {
    return padrao
  }

  return valor
}
